import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  Dumbbell,
  Sparkles,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";

const HERO_IMAGE =
  "https://customer-assets-0z36b82j.emergentagent.net/job_movimento-saude-1/artifacts/jx8i36fs_Osteopatia.jpeg";

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.75, ease: "easeOut" },
};

export default function FisioterapiaPage() {
  const { t } = useT();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const howItems = t("fisioPage.howItems");
  const techItems = t("fisioPage.techItems");
  const gymItems = t("fisioPage.gymItems");

  return (
    <div className="App bg-[#FEFEFE]" data-testid="fisio-page">
      <Navbar />

      {/* ═══════ HERO ═══════ */}
      <section
        data-testid="fisio-hero"
        className="relative min-h-[78vh] lg:min-h-[80vh] w-full overflow-hidden bg-[#0F1A17]"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Fisioterapia na Yin Motion"
            className="w-full h-full object-cover object-[center_45%] opacity-[0.9]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A17]/75 via-[#0F1A17]/45 to-[#0F1A17]/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A17]/50 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 min-h-[78vh] lg:min-h-[80vh] flex flex-col">
          <div className="flex-1 flex items-end pb-20 md:pb-28 pt-36 md:pt-44">
            <div className="w-full max-w-[1200px] mx-auto px-5 md:px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="max-w-[860px]"
              >
                <Link
                  to="/"
                  data-testid="fisio-back"
                  className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-white/70 hover:text-[#EE8984] transition-colors mb-10"
                >
                  <ArrowLeft size={14} />
                  {t("fisioPage.back")}
                </Link>

                <div className="flex items-center gap-3 mb-8">
                  <span className="h-px w-10 bg-[#EE8984]" />
                  <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#F7DEDA]">
                    {t("fisioPage.eyebrow")}
                  </span>
                </div>

                <h1 className="font-serif text-[34px] sm:text-[44px] lg:text-[56px] xl:text-[64px] leading-[1.1] tracking-tight text-white font-light">
                  {t("fisioPage.heroTitle")}
                </h1>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ INTRO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          <motion.div {...fade} className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
                {t("fisioPage.introEyebrow")}
              </span>
            </div>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-8">
            <p className="font-serif text-[22px] md:text-[28px] lg:text-[32px] leading-[1.4] tracking-tight text-[#2A322C] font-light">
              {t("fisioPage.introBody")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ COMO TRABALHAMOS ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#EEF2E9]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div {...fade} className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
                {t("fisioPage.howEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[40px] lg:text-[46px] leading-[1.15] tracking-tight text-[#2A322C] font-semibold">
              {t("fisioPage.howTitle")}
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-[#5C6660] leading-[1.85] max-w-[480px]">
              {t("fisioPage.howLead")}
            </p>
          </motion.div>

          <motion.ul {...fade} className="lg:col-span-7 space-y-5">
            {howItems.map((item, i) => (
              <li
                key={i}
                data-testid={`how-item-${i}`}
                className="group flex items-start gap-5 bg-white/70 backdrop-blur-sm border border-[#A3BD98]/15 rounded-[12px] px-7 py-6 hover:bg-white hover:border-[#A3BD98]/35 hover:-translate-y-[2px] transition-all duration-300 shadow-[0_6px_18px_-14px_rgba(42,50,44,0.12)]"
              >
                <span className="shrink-0 mt-[6px] w-5 h-5 rounded-full bg-[#A3BD98] flex items-center justify-center text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="text-[16px] md:text-[17px] text-[#2A322C] leading-[1.6] font-medium">
                  {item}
                </span>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ═══════ TECNOLOGIAS ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6">
          <motion.div {...fade} className="max-w-3xl mb-14 md:mb-20">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#EE8984]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#CF8376]">
                {t("fisioPage.techEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[40px] lg:text-[46px] leading-[1.15] tracking-tight text-[#2A322C] font-semibold">
              {t("fisioPage.techTitle")}
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-[#5C6660] leading-[1.85]">
              {t("fisioPage.techLead")}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {techItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`tech-item-${i}`}
                className="group relative bg-[#FEFEFE] border border-[#A3BD98]/20 rounded-[14px] overflow-hidden hover:border-[#A3BD98]/45 hover:-translate-y-[3px] transition-all duration-300 shadow-[0_8px_22px_-16px_rgba(42,50,44,0.12)]"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#EEF2E9]/40">
                  <img
                    src={item.image}
                    alt={item.name}
                    loading="lazy"
                    style={item.imagePosition ? { objectPosition: item.imagePosition } : undefined}
                    className="w-full h-full object-cover opacity-[0.92] saturate-[0.85] group-hover:scale-[1.06] group-hover:opacity-100 group-hover:saturate-100 transition-all duration-[700ms] ease-out"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-[#FEFEFE]/20 via-transparent to-transparent group-hover:opacity-0 transition-opacity duration-500"
                  />
                </div>
                <div className="px-6 py-5 md:px-7 md:py-6">
                  <h3 className="font-serif text-[16px] md:text-[18px] text-[#2A322C] font-semibold leading-[1.3] tracking-tight">
                    {item.name}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ GINÁSIO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#0F1A17] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(163,189,152,0.1),transparent_55%)] pointer-events-none"
        />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div {...fade} className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]/70" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#A3BD98]/80">
                {t("fisioPage.gymEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[40px] lg:text-[46px] leading-[1.15] tracking-tight text-white font-semibold">
              {t("fisioPage.gymTitle")}
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-white/70 leading-[1.85] max-w-[480px]">
              {t("fisioPage.gymLead")}
            </p>
          </motion.div>

          <motion.ul {...fade} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 self-center">
            {gymItems.map((item, i) => {
              const inner = (
                <>
                  <span className="shrink-0 w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-[#A3BD98] group-hover:bg-[#A3BD98] group-hover:text-white transition-colors duration-300 overflow-hidden">
                    {item.icon ? (
                      <img
                        src={item.icon}
                        alt=""
                        aria-hidden="true"
                        className="w-6 h-6 object-contain transition-[filter] duration-300 invert opacity-90 group-hover:opacity-100"
                      />
                    ) : (
                      <Dumbbell size={15} strokeWidth={1.75} />
                    )}
                  </span>
                  <span className="flex-1 text-[15px] md:text-[16px] text-white font-medium leading-[1.45]">
                    {item.name}
                  </span>
                  {item.href && (
                    <ArrowUpRight
                      size={16}
                      className="shrink-0 text-white/40 group-hover:text-[#A3BD98] group-hover:rotate-45 transition-all duration-300"
                    />
                  )}
                </>
              );

              const baseClass =
                "group flex items-center gap-4 border border-white/15 rounded-[10px] px-5 py-5 transition-all duration-300";

              if (item.href) {
                return (
                  <li key={i} className="list-none">
                    <Link
                      to={item.href}
                      data-testid={`gym-item-${i}`}
                      className={`${baseClass} w-full hover:bg-white/[0.06] hover:border-[#A3BD98]/60 hover:-translate-y-[2px] cursor-pointer`}
                    >
                      {inner}
                    </Link>
                  </li>
                );
              }

              return (
                <li
                  key={i}
                  data-testid={`gym-item-${i}`}
                  className={`${baseClass} hover:bg-white/[0.04] hover:border-[#A3BD98]/50`}
                >
                  {inner}
                </li>
              );
            })}
          </motion.ul>
        </div>
      </section>

      {/* ═══════ COMPROMISSO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <motion.div
          {...fade}
          className="max-w-[900px] mx-auto px-5 md:px-6 text-center"
        >
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#A3BD98]" />
            <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
              {t("fisioPage.commitEyebrow")}
            </span>
            <span className="h-px w-10 bg-[#A3BD98]" />
          </div>
          <Sparkles
            size={28}
            strokeWidth={1.5}
            className="text-[#A3BD98] mx-auto mb-6"
          />
          <h2 className="font-serif text-[30px] md:text-[38px] lg:text-[44px] leading-[1.2] tracking-tight text-[#2A322C] font-semibold">
            {t("fisioPage.commitTitle")}
          </h2>
          <p className="mt-7 text-[17px] md:text-[19px] text-[#5C6660] leading-[1.85] font-light max-w-2xl mx-auto">
            {t("fisioPage.commitLead")}
          </p>
        </motion.div>
      </section>

      {/* ═══════ CTA FINAL ═══════ */}
      <section className="relative py-24 md:py-32 bg-[#EEF2E9]">
        <motion.div
          {...fade}
          className="max-w-[900px] mx-auto px-5 md:px-6 text-center"
        >
          <p className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD] mb-6">
            {t("fisioPage.ctaEyebrow")}
          </p>
          <h3 className="font-serif text-[26px] md:text-[34px] lg:text-[40px] leading-[1.25] tracking-tight text-[#2A322C] font-light max-w-2xl mx-auto">
            {t("fisioPage.ctaTitle")}
          </h3>
          <div className="mt-10 flex items-center justify-center">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fisio-cta"
              className="group inline-flex items-center gap-2 bg-[#EE8984] text-white rounded-full px-9 py-[18px] text-[14px] font-semibold tracking-wide hover:bg-[#CF8376] hover:scale-[1.02] hover:shadow-[0_16px_40px_-12px_rgba(238,137,132,0.55)] transition-all duration-300"
            >
              {t("fisioPage.ctaButton")}
              <ArrowUpRight
                size={17}
                className="group-hover:rotate-45 transition-transform duration-300"
              />
            </a>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
